import React from "react";
import { Box, Divider } from "@mui/material";
import ResumeTextRow from "./ResumeTextRow";
import EmptySkleton from "../components/EmptySkleton";

const PREDEFINED_SECTIONS = ["Special Skills", "Training", "Bio"];

const hasContent = (section) =>
  section &&
  typeof section.resumeTextDetails === "string" &&
  section.resumeTextDetails.trim() !== "";

const ResumeTextSections = ({
  resumeSections = [],
  resumeEditMode = false,
  setEditInitialData,
  setSectionToDelete,
  setDeleteDialogOpen,
  setEditPromptOpen,
  showEmptySections = true,
}) => {
  const handleAddNew = (sectionName = "") => {
    const existing = resumeSections.find(
      (s) =>
        typeof s.resumeTextHeading === "string" &&
        s.resumeTextHeading.toLowerCase() === sectionName.toLowerCase()
    );

    if (existing) {
      setEditInitialData(existing);
    } else {
      setEditInitialData({
        resumeTextDataId: 0,
        resumeSectionId: 0,
        resumeTextHeading: sectionName,
        resumeTextDetails: "",
        sortOrder: 0,
        isInternal: 1,
      });
    }

    setEditPromptOpen(true);
  };

  const handleEdit = (section) => {
    setEditInitialData(section);
    setEditPromptOpen(true);
  };

  const handleDelete = (section) => {
    setSectionToDelete(section);
    setDeleteDialogOpen(true);
  };

  const activeSections = resumeSections.filter(
    (s) => s.activeFlag !== false && typeof s.resumeTextHeading === "string"
  );

  const sectionMap = activeSections.reduce((acc, section) => {
    acc[section.resumeTextHeading.toLowerCase()] = section;
    return acc;
  }, {});

  const userCustomSections = activeSections.filter(
    (s) =>
      !PREDEFINED_SECTIONS.some(
        (p) => p.toLowerCase() === s.resumeTextHeading.toLowerCase()
      )
  );

  const rows = [];

  // Predefined Sections
  PREDEFINED_SECTIONS.forEach((title) => {
    const existing = sectionMap[title.toLowerCase()];

    if (!existing || !hasContent(existing)) {
      if (!showEmptySections) {
        return;
      }

      rows.push({
        key: `empty-${title}`,
        node: (
          <EmptySkleton
            title={title}
            editMode={resumeEditMode}
            onAdd={() => handleAddNew(title)}
            showDivider={false}
            alwaysVisible={true}
          />
        ),
      });
    } else {
      rows.push({
        key: `section-${existing.resumeTextDataId}`,
        node: (
          <ResumeTextRow
            section={existing}
            resumeEditMode={resumeEditMode}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ),
      });
    }
  });

  // User Custom Sections
  userCustomSections.forEach((section) => {
    if (!hasContent(section)) return;

    rows.push({
      key: `custom-${section.resumeTextDataId}`,
      node: (
        <ResumeTextRow
          section={section}
          resumeEditMode={resumeEditMode}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    });
  });

  // Add Custom Section button (edit mode only)
  if (resumeEditMode) {
    rows.push({
      key: "add-custom",
      node: (
        <EmptySkleton
          title="Custom Section"
          editMode={true}
          onAdd={() => handleAddNew("")}
          showDivider={false}
        />
      ),
    });
  }

  return (
    <Box sx={{ backgroundColor: "#fff", borderRadius: "5px", mt: "1px" }}>
      {rows.map((row, index) => (
        <React.Fragment key={row.key}>
          {index > 0 && <Divider />}
          {row.node}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ResumeTextSections;